import React, { useCallback, useMemo, useRef } from 'react'
import { Pagination } from '@surecompanies/core_components'
import {
  ColDef,
  RowClassRules,
  SortChangedEvent,
} from '@ag-grid-community/core'
import { AgGridReact } from '@ag-grid-community/react'

import { VCN_CARD_ACTION_MAX } from '../../api/dto/vcn-card-action.dto'
import { VcnCardInUseStatus, VcnCardListSortBy, VcnSortOrder } from './types'
import type {
  VcnCardListSort,
  PaginationState,
  VcnManagementCardRow,
} from './types'
import { cardKeyOf } from './vcnManagementCardKey'
import { usePaginatedSelection } from './hooks/usePaginatedSelection'
import {
  CardBenefitIdCellRenderer,
  InsuredPersonCellRenderer,
  PrimaryPaymentMethodCellRenderer,
  VcnCardInUseCellRenderer,
  VcnProviderCellRenderer,
  VcnStatusCellRenderer,
  VcnTokenCellRenderer,
} from './VcnManagementTableCellTags'
import {
  GridViewport,
  PaginationContainer,
  TableShell,
} from './VcnManagementLayout.styles'
import { useFitViewportHeight } from './hooks/useFitViewportHeight'

const tagCellStyle = {
  display: 'flex',
  alignItems: 'center',
} as const

/**
 * Column defaults, exported so the ordering contract can be asserted.
 *
 * The server owns the order. Without the no-op comparator ag-grid re-sorts the
 * page it holds once the response lands, destroying that order — and it looks
 * right on page 1, because re-sorting 25 already-sorted rows changes nothing.
 * It diverges from page 2 onward, or wherever ties fall differently. Same
 * approach as Application Engine's grid.
 *
 * `sortable` is false by default rather than blanket true: only the columns EPS
 * accepts a sort for may be clicked, and a blanket true is exactly what made the
 * shipped version reorder 25 rows out of thousands.
 */
export const CARD_TABLE_DEFAULT_COL_DEF: ColDef<VcnManagementCardRow> = {
  resizable: true,
  comparator: () => 0,
  sortable: false,
}

/** ag-grid's own arrow direction for a sort order. */
function arrowFor(order: VcnSortOrder): 'asc' | 'desc' {
  return order === VcnSortOrder.ASC ? 'asc' : 'desc'
}

/**
 * The columns EPS accepts a sort for, keyed by grid field.
 *
 * Every field here happens to equal its enum value, so a cast would compile —
 * but it would also wave through any other column id, which EPS rejects. The
 * lookup fails closed to unsorted instead.
 */
const SORTABLE_COLUMN_BY_FIELD: Record<string, VcnCardListSortBy | undefined> =
  Object.fromEntries(
    Object.values(VcnCardListSortBy).map((field) => [field, field])
  )

export interface VcnManagementCardTableProps {
  /** What the server was asked to order by, or null for newest first. */
  activeSort: VcnCardListSort | null
  onSortChange: (next: VcnCardListSort | null) => void
  rows: VcnManagementCardRow[]
  emptyText: React.ReactNode
  /** The full selection, including cards on pages not currently loaded. */
  selectedRows: VcnManagementCardRow[]
  pagination: PaginationState
  onRowSelectionChange: (rows: VcnManagementCardRow[]) => void
  onPaginationChange: (page: number, pageSize: number) => void
}

/**
 * Card Mode — one row per VCN card.
 *
 * Distinct from `VcnManagementBenefitTable` (one row per benefit) rather than a mode of
 * it: the two tabs each render one entity, which is what makes a tile's count
 * equal the rows it filters. Two consequences visible here —
 *
 * - **No master-detail.** The expandable "Associated VCNs" panel is Benefit
 *   Mode's, where a row is an employee's benefit and can hold several cards.
 *   Here a row already *is* a card.
 * - **Benefit ID leads and carries the in-use state**, so there is no separate
 *   `Plan` column. See {@link CardBenefitIdCellRenderer}.
 *
 * Selection is keyed on `provider:virtualCardKey` and survives paging — see
 * {@link usePaginatedSelection}. Terminate addresses both of those fields, so
 * what the admin ticks is exactly what EPS acts on, with no benefit-level
 * resolution in between. The token alone is not unique across providers.
 */
export default function VcnManagementCardTable({
  rows,
  emptyText,
  selectedRows,
  pagination,
  activeSort,
  onRowSelectionChange,
  onPaginationChange,
  onSortChange,
}: VcnManagementCardTableProps) {
  const gridRef = useRef<AgGridReact<VcnManagementCardRow>>(null)

  const { onSelectionChanged } = usePaginatedSelection({
    gridRef,
    rows,
    selectedRows,
    keyOf: cardKeyOf,
    maxSelection: VCN_CARD_ACTION_MAX,
    onSelectionChange: onRowSelectionChange,
  })
  /** Marks a column sortable and shows the arrow for whatever the server was asked for. */
  const sortableColumn = useCallback(
    (field: VcnCardListSortBy): Partial<ColDef<VcnManagementCardRow>> => ({
      sortable: true,
      // Driven from state, not ag-grid's own: the arrow must show what the list
      // was actually ordered by, and survive a remount.
      sort: activeSort?.sort === field ? arrowFor(activeSort.order) : null,
    }),
    [activeSort]
  )

  const columnDefs = useMemo<ColDef<VcnManagementCardRow>[]>(
    () => [
      {
        headerName: '',
        width: 52,
        pinned: 'left',
        checkboxSelection: true,
        // Header checkbox selects the loaded page only, which is all ag-grid
        // holds — never the whole filtered set.
        headerCheckboxSelection: true,
        suppressMovable: true,
        sortable: false,
      },
      {
        // First data column at the PM's request (2026-09-15 demo): the token is
        // what an admin arrives with when chasing a specific card, so it is the
        // column they scan. Fixed width rather than flex — it led to the grid
        // giving the id all the slack and squeezing the names beside it.
        field: 'vcnToken',
        headerName: 'Token ID',
        ...sortableColumn(VcnCardListSortBy.VCN_TOKEN),
        width: 190,
        cellRenderer: VcnTokenCellRenderer,
      },
      {
        field: 'insuredPersonName',
        headerName: 'Insured person',
        ...sortableColumn(VcnCardListSortBy.INSURED_PERSON_NAME),
        flex: 1,
        minWidth: 150,
        cellRenderer: InsuredPersonCellRenderer,
      },
      {
        field: 'benefitId',
        headerName: 'Benefit ID',
        ...sortableColumn(VcnCardListSortBy.BENEFIT_ID),
        width: 190,
        cellStyle: tagCellStyle,
        cellRenderer: CardBenefitIdCellRenderer,
      },
      {
        // Header renamed from Benefit Mode's "Company" over the same field —
        // the employer of the card HOLDER, not a client public key.
        field: 'companyName',
        headerName: 'Client',
        ...sortableColumn(VcnCardListSortBy.COMPANY_NAME),
        flex: 1,
        minWidth: 160,
        valueFormatter: ({ value }) => value || '—',
      },
      {
        // Absent for an orphan card: carrier is a property of the plan, and a
        // card carries no plan reference.
        field: 'carrierName',
        headerName: 'Carrier',
        ...sortableColumn(VcnCardListSortBy.CARRIER_NAME),
        width: 130,
        valueFormatter: ({ value }) => value || '—',
      },
      {
        field: 'primaryPaymentMethod',
        headerName: 'Payment method',
        ...sortableColumn(VcnCardListSortBy.PRIMARY_PAYMENT_METHOD),
        width: 140,
        cellStyle: tagCellStyle,
        cellRenderer: PrimaryPaymentMethodCellRenderer,
      },
      {
        field: 'provider',
        headerName: 'Provider',
        ...sortableColumn(VcnCardListSortBy.PROVIDER),
        width: 130,
        cellStyle: tagCellStyle,
        cellRenderer: VcnProviderCellRenderer,
      },
      {
        // The CARD's own status. Benefit Mode's same-looking column is the
        // BENEFIT's, which is why the field is `cardStatus` here.
        field: 'cardStatus',
        headerName: 'VCN status',
        ...sortableColumn(VcnCardListSortBy.CARD_STATUS),
        width: 130,
        cellStyle: tagCellStyle,
        cellRenderer: VcnStatusCellRenderer,
      },
      {
        // Whether a benefit is using this card. Orphan cleanup filters on it,
        // so it has to be readable in the grid, not just in the drawer.
        field: 'inUse',
        headerName: 'Plan',
        ...sortableColumn(VcnCardListSortBy.IN_USE),
        width: 130,
        cellStyle: tagCellStyle,
        cellRenderer: VcnCardInUseCellRenderer,
      },
      {
        field: 'lastFourDigits',
        headerName: 'Last 4',
        ...sortableColumn(VcnCardListSortBy.LAST_FOUR_DIGITS),
        width: 90,
        valueFormatter: ({ value }) => value || '—',
      },
    ],
    // Rebuilt when the sort changes so each column's arrow reflects it.
    [sortableColumn]
  )

  const handleSortChanged = useCallback(
    (event: SortChangedEvent<VcnManagementCardRow>) => {
      // One column at a time — `suppressMultiSort` on the grid keeps it that
      // way, so there is never more than one to find here.
      const sorted = event.api.getColumnState().find((column) => column.sort)
      const sortBy = SORTABLE_COLUMN_BY_FIELD[sorted?.colId ?? '']
      if (!sortBy) {
        // Third click clears the column: back to the server's newest-first.
        // Also covers a column EPS does not accept a sort for, which would
        // otherwise be sent through and rejected.
        onSortChange(null)
        return
      }
      onSortChange({
        sort: sortBy,
        order: sorted?.sort === 'desc' ? VcnSortOrder.DESC : VcnSortOrder.ASC,
      })
    },
    [onSortChange]
  )

  const rowClassRules = useMemo<RowClassRules<VcnManagementCardRow>>(
    () => ({
      'vcn-row-ended': ({ data }) =>
        data?.inUse === VcnCardInUseStatus.IN_USE_ENDED,
    }),
    []
  )

  // Pagination sits below the grid inside the shell, so the shell is what has
  // to end at the viewport bottom — the grid then flexes into what is left.
  // Layout above the table (banners, alerts, wrapping toolbar) is watched by
  // the hook; do not list those causes here.
  const { setNode: setShellNode, height: shellHeight } = useFitViewportHeight()

  return (
    <TableShell innerRef={setShellNode} style={{ height: shellHeight }}>
      <GridViewport className="ag-theme-alpine">
        <AgGridReact<VcnManagementCardRow>
          ref={gridRef}
          // EPS takes a single `sort`, so shift-click multi-sort is turned off
          // rather than interpreted: it would leave arrows on two headers while
          // the list was ordered by whichever column sat leftmost — not even
          // necessarily the one just clicked.
          suppressMultiSort
          rowData={rows}
          columnDefs={columnDefs}
          defaultColDef={CARD_TABLE_DEFAULT_COL_DEF}
          rowClassRules={rowClassRules}
          getRowId={(params) => (params.data ? cardKeyOf(params.data) : '')}
          rowSelection="multiple"
          onSelectionChanged={onSelectionChanged}
          onSortChanged={handleSortChanged}
          // Clicking a row must not select it: the row is a link target
          // (Insured person) and a copy target (token, Benefit ID).
          suppressRowClickSelection
          suppressCellFocus
          headerHeight={44}
          noRowsOverlayComponent={() => (
            <div className="ag-overlay-no-rows-center" style={{ padding: 16 }}>
              {emptyText}
            </div>
          )}
        />
      </GridViewport>
      {pagination.total > 0 ? (
        <PaginationContainer>
          <Pagination
            current={pagination.page}
            total={pagination.total}
            pageSize={pagination.pageSize}
            pageSizeOptions={[25, 50, 100, 200]}
            showSizeChanger
            showTotal={(total, range) =>
              `Showing ${range[0]}-${range[1]} of ${total} cards`
            }
            onChange={onPaginationChange}
          />
        </PaginationContainer>
      ) : null}
    </TableShell>
  )
}
